import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { Trans, useTranslation } from 'react-i18next';
import cameraOcrVideo from 'assets/img/knowledge/3_camera_ocr.mp4';
import cameraOcrPoster from 'assets/img/knowledge/3_camera_ocr_poster.jpg';
import testimonialBoy from 'assets/img/testimonial_boy.webp';
import pluginDemoVideoSm from 'assets/img/knowledge/9_plugin_demo_sm.mp4';
import pluginDemoSmPoster from 'assets/img/knowledge/9_plugin_demo_sm_poster.jpg';
import pluginDemoVideo from 'assets/img/knowledge/9_plugin_demo.mp4';
import webappDemoVideoSm from 'assets/img/knowledge/10_webapp_demo_sm.mp4';
import webappDemoSmPoster from 'assets/img/knowledge/10_webapp_demo_sm_poster.jpg';
import webappDemoVideo from 'assets/img/knowledge/10_webapp_demo.mp4';
import {
  ConfigProvider,
  DEFAULT_CONFIG,
  useConfig,
  type StripesConfig,
} from '../components/Canvas/StripedComponents';
import { StripedTextarea } from '../components/Canvas/StripedTextarea';

import {
  ensureDysGoEmbedScriptLoaded,
  setDysGoDemoVisible,
  tryInitPlugin,
  disablePlugin,
  cleanupPluginDOM,
} from '../utils/dysgoPlugin';

/* ── Constants ── */

const PUNCTUATION_REGEX = /[.,!?;:()–—*\/£$%&=\^_§°]/;

const DEFAULT_SAMPLE_TEXT_EN = [
  'This is your reading view.',
  'Each stripe is one line — it gives your eyes a clear lane to follow.',
  '',
  'Try changing the preset above, or just start typing your own text here.',
  'Paste a paragraph from an article, a homework prompt, or anything you want to read.',
].join('\n');

/** Duration in ms for the drop-zone toast before redirect */
const DROP_TOAST_DURATION = 2200;

/* ── Utilities ── */

function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/** Returns true when the user prefers reduced motion at the OS level. */
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function useAnchorSmoothScroll() {
  useEffect(() => {
    const onClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      const isHash = href.startsWith('#');
      const isRootHash = href.startsWith('/#');
      if (!isHash && !isRootHash) return;

      const hash = isHash ? href : href.slice(1);
      const id = hash.startsWith('#') ? hash : `#${hash}`;
      const el = document.querySelector(id);
      if (!el) return;

      e.preventDefault();
      window.history.pushState(null, '', href);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
}

/* ── Presets & Themes ── */

type PresetId = 'comfort' | 'focus' | 'high_contrast';
type ThemeId = 'ocean' | 'mint' | 'sand' | 'mono';

function presetToConfig(preset: PresetId): Partial<StripesConfig> {
  switch (preset) {
    case 'focus':
      return {
        colorA: '#E9F4FF',
        colorB: '#FFFFFF',
        fontSize: 20,
        letterSpacing: 0.5,
        factor: 3.5,
        uppercase: true,
        color: '#0B1220',
      };
    case 'high_contrast':
      return {
        colorA: '#F2F2F2',
        colorB: '#FFFFFF',
        fontSize: 20,
        letterSpacing: 0.7,
        factor: 3.6,
        uppercase: true,
        color: '#000000',
      };
    case 'comfort':
    default:
      return {
        colorA: DEFAULT_CONFIG.colorA,
        colorB: DEFAULT_CONFIG.colorB,
        fontSize: 17,
        letterSpacing: 0.3,
        factor: 3.2,
        uppercase: true,
        color: '#0B1220',
      };
  }
}

function themeToConfig(theme: ThemeId): Partial<StripesConfig> {
  switch (theme) {
    case 'mint':
      return { colorA: '#D8F7EA', colorB: '#FFFFFF', color: '#0B1220' };
    case 'sand':
      return { colorA: '#F9E7D0', colorB: '#FFFFFF', color: '#0B1220' };
    case 'mono':
      return { colorA: '#F2F2F2', colorB: '#FFFFFF', color: '#0B1220' };
    case 'ocean':
    default:
      return { colorA: DEFAULT_CONFIG.colorA, colorB: DEFAULT_CONFIG.colorB, color: '#0B1220' };
  }
}

/* ── PlaygroundControls ── */

function PlaygroundControls({
  highlightPunctuation,
  onHighlightPunctuation,
}: {
  highlightPunctuation: boolean;
  onHighlightPunctuation: (next: boolean) => void;
}) {
  const { config, update, setConfig, linePx } = useConfig();
  const [theme, setTheme] = useState<ThemeId>('ocean');
  const [activePreset, setActivePreset] = useState<PresetId | null>('comfort');

  const applyPreset = (preset: PresetId) => {
    setActivePreset(preset);
    setConfig({ ...DEFAULT_CONFIG, ...presetToConfig(preset) });
  };

  const applyTheme = (next: ThemeId) => {
    setTheme(next);
    const patch = themeToConfig(next);
    setConfig({ ...config, ...patch });
  };

  return (
    <div className="lf-controls" role="group" aria-label="Reading format controls">
      <div className="lf-seg" role="group" aria-label="Reading presets">
        <button type="button" className={`lf-seg-btn${activePreset === 'comfort' ? ' active' : ''}`} onClick={() => applyPreset('comfort')} aria-pressed={activePreset === 'comfort'}>
          Comfort
        </button>
        <button type="button" className={`lf-seg-btn${activePreset === 'focus' ? ' active' : ''}`} onClick={() => applyPreset('focus')} aria-pressed={activePreset === 'focus'}>
          Focus
        </button>
        <button type="button" className={`lf-seg-btn${activePreset === 'high_contrast' ? ' active' : ''}`} onClick={() => applyPreset('high_contrast')} aria-pressed={activePreset === 'high_contrast'}>
          High contrast
        </button>
      </div>

      <div className="lf-theme-row" role="group" aria-label="Color themes">
        <button
          type="button"
          className={`lf-theme-btn ${theme === 'ocean' ? 'active' : ''}`}
          onClick={() => applyTheme('ocean')}
          aria-pressed={theme === 'ocean'}
        >
          <span className="lf-swatch" style={{ background: 'linear-gradient(90deg, #ADD8E6 0 55%, #FFFFFF 55% 100%)' }} aria-hidden="true" />
          Ocean
        </button>
        <button
          type="button"
          className={`lf-theme-btn ${theme === 'mint' ? 'active' : ''}`}
          onClick={() => applyTheme('mint')}
          aria-pressed={theme === 'mint'}
        >
          <span className="lf-swatch" style={{ background: 'linear-gradient(90deg, #D8F7EA 0 55%, #FFFFFF 55% 100%)' }} aria-hidden="true" />
          Mint
        </button>
        <button
          type="button"
          className={`lf-theme-btn ${theme === 'sand' ? 'active' : ''}`}
          onClick={() => applyTheme('sand')}
          aria-pressed={theme === 'sand'}
        >
          <span className="lf-swatch" style={{ background: 'linear-gradient(90deg, #F9E7D0 0 55%, #FFFFFF 55% 100%)' }} aria-hidden="true" />
          Sand
        </button>
        <button
          type="button"
          className={`lf-theme-btn ${theme === 'mono' ? 'active' : ''}`}
          onClick={() => applyTheme('mono')}
          aria-pressed={theme === 'mono'}
        >
          <span className="lf-swatch" style={{ background: 'linear-gradient(90deg, #F2F2F2 0 55%, #FFFFFF 55% 100%)' }} aria-hidden="true" />
          Mono
        </button>
      </div>

      <div className="lf-grid">
        <label className="lf-field">
          <span className="lf-field-label">Font size</span>
          <input
            type="range"
            min={14}
            max={26}
            value={config.fontSize}
            aria-label="Font size"
            onChange={(e) => { setActivePreset(null); update({ fontSize: clampNumber(Number(e.target.value || 18), 14, 26) }); }}
          />
        </label>

        <label className="lf-field">
          <span className="lf-field-label">Line height</span>
          <input
            type="range"
            min={2.6}
            max={4.8}
            step={0.1}
            value={config.factor}
            aria-label="Line height"
            onChange={(e) => { setActivePreset(null); update({ factor: clampNumber(Number(e.target.value || 3.8), 2.6, 4.8) }); }}
          />
        </label>

        <label className="lf-field">
          <span className="lf-field-label">Letter spacing</span>
          <input
            type="range"
            min={0}
            max={1.5}
            step={0.1}
            value={config.letterSpacing}
            aria-label="Letter spacing"
            onChange={(e) => { setActivePreset(null); update({ letterSpacing: clampNumber(Number(e.target.value || 0), 0, 1.5) }); }}
          />
        </label>
      </div>

      <div className="lf-color-row">
        <div className="lf-color-field">
          <span className="lf-field-label">Stripe A</span>
          <span className="lf-color-swatch" style={{ background: config.colorA }}>
            <input
              type="color"
              value={config.colorA}
              onChange={(e) => {
                setTheme('ocean');
                update({ colorA: e.target.value });
              }}
              aria-label="Stripe color A"
            />
          </span>
        </div>
        <div className="lf-color-field">
          <span className="lf-field-label">Stripe B</span>
          <span className="lf-color-swatch" style={{ background: config.colorB }}>
            <input
              type="color"
              value={config.colorB}
              onChange={(e) => {
                setTheme('ocean');
                update({ colorB: e.target.value });
              }}
              aria-label="Stripe color B"
            />
          </span>
        </div>
      </div>

      <label className="lf-toggle">
        <input
          type="checkbox"
          checked={highlightPunctuation}
          onChange={(e) => onHighlightPunctuation(e.target.checked)}
        />
        <span>Highlight punctuation</span>
      </label>
    </div>
  );
}

/* ── PlainTextPreview ── */

function PlainTextPreview({ text }: { text: string }) {
  return (
    <div className="lf-plain-text" role="region" aria-label="Plain text (no formatting)">
      {text}
    </div>
  );
}

/* ── DropToast ── */

function DropToast({ message, visible }: { message: string; visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="lf-drop-toast" role="alert" aria-live="assertive">
      <i className="fas fa-sign-in-alt" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

/* ── BetaModal (replaces store links) ── */

function BetaModal({
  open,
  onClose,
  t,
}: {
  open: boolean;
  onClose: () => void;
  t: (key: string, fallback: string) => string;
}) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setSubmitted(false);
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation this would POST to an API
    setSubmitted(true);
  };

  return (
    <div
      className="lf-modal-backdrop"
      onClick={() => onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={t('landing.beta_modal_title', 'Closed beta - early access')}
    >
      <div className="lf-modal lf-beta-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lf-modal-head">
          <b>{t('landing.beta_modal_title', 'Closed beta - early access')}</b>
          <button
            type="button"
            className="lf-modal-close"
            onClick={() => onClose()}
            aria-label={t('landing.close', 'Close')}
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          {submitted ? (
            <div role="status">
              <i className="fas fa-check-circle" style={{ fontSize: '2rem', color: 'var(--lf-green)', marginBottom: '0.75rem', display: 'block' }} aria-hidden="true" />
              <p style={{ fontWeight: 700, fontSize: '1.05rem', margin: '0 0 0.5rem' }}>
                {t('landing.beta_thanks', 'You are on the list!')}
              </p>
              <p style={{ color: 'var(--lf-muted)', fontSize: '0.92rem', margin: 0 }}>
                {t('landing.beta_thanks_desc', 'We will notify you when the mobile app is available.')}
              </p>
            </div>
          ) : (
            <>
              <p style={{ color: 'var(--lf-muted)', fontSize: '0.95rem', margin: '0 0 1.25rem', lineHeight: 1.55 }}>
                {t(
                  'landing.beta_desc',
                  'The mobile app is currently in closed beta. Enter your email to get early access when it launches.',
                )}
              </p>
              <form onSubmit={handleSubmit} style={{ maxWidth: 360, margin: '0 auto' }}>
                <label htmlFor="beta-email" className="sr-only">
                  {t('landing.beta_email_label', 'Email address')}
                </label>
                <input
                  id="beta-email"
                  type="email"
                  required
                  className="lf-textarea"
                  style={{ minHeight: 'auto', padding: '0.65rem 1rem', marginBottom: '0.75rem' }}
                  placeholder={t('landing.beta_email_placeholder', 'you@example.com')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  style={{ borderRadius: '999px', fontWeight: 700, width: '100%' }}
                >
                  {t('landing.beta_submit', 'Get early access')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */

type ModalKey = 'mobile' | 'web' | 'plugin';

const Landing2026Funnel2: React.FC = () => {
  const { t } = useTranslation();

  const [highlightPunctuation, setHighlightPunctuation] = useState(true);

  useAnchorSmoothScroll();

  /* ── Page meta / OG tags ── */
  useEffect(() => {
    const pageTitle = 'DysGo — Dyslexia-Friendly Reading Tool Suite';
    const pageDesc = 'Transform any text into a dyslexia-friendly format. Adjust spacing, size, and rhythm on mobile, desktop, or any website.';
    document.title = pageTitle;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('property', 'og:title', pageTitle);
    setMeta('property', 'og:description', pageDesc);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:image', `${window.location.origin}/assets/img/og-preview.png`);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', pageTitle);
    setMeta('name', 'twitter:description', pageDesc);
  }, []);

  const renderPunctuated = (text: string) => {
    return Array.from(text).map((char, index) => {
      if (PUNCTUATION_REGEX.test(char)) {
        return (
          <span className="punctuation" key={`p-${index}`}>
            {char}
          </span>
        );
      }
      return char;
    });
  };

  const sampleTextInitial = useMemo(() => {
    const translated = t('landing.demo_text_2', DEFAULT_SAMPLE_TEXT_EN);
    return translated.startsWith('landing.') ? DEFAULT_SAMPLE_TEXT_EN : translated;
  }, [t]);

  const stripedInitialConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...presetToConfig('comfort') }), []);
  const [sampleText, setSampleText] = useState(sampleTextInitial);

  const userEditedRef = useRef(false);
  useEffect(() => {
    if (!userEditedRef.current) setSampleText(sampleTextInitial);
  }, [sampleTextInitial]);

  /* ── Feature 1: Drop zone with toast ── */
  const [dropHover, setDropHover] = useState(false);
  const [dropToastVisible, setDropToastVisible] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDropHover(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setDropHover(false);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropHover(false);
    dragCounterRef.current = 0;
    // Show toast, then redirect after a brief delay
    setDropToastVisible(true);
    setTimeout(() => {
      window.location.href = '/app/auth/login';
    }, DROP_TOAST_DURATION);
  };

  /* ── Feature 3: Demo modal ── */
  const [activeModal, setActiveModal] = useState<'mobile' | 'web' | 'plugin' | null>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);
  const [modalPlaying, setModalPlaying] = useState(true);

  const toggleModalPlay = useCallback(() => {
    const v = modalVideoRef.current;
    if (!v) return;
    if (modalPlaying) { v.pause(); setModalPlaying(false); }
    else { v.play().catch(() => {}); setModalPlaying(true); }
  }, [modalPlaying]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!activeModal) {
      modalVideoRef.current?.pause();
      setModalPlaying(false);
      return;
    }
    // Reset to playing when modal opens (videos have autoPlay)
    setModalPlaying(!prefersReducedMotion());
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [activeModal]);

  /* ── Beta modal (replaces store links) ── */
  const [betaModalOpen, setBetaModalOpen] = useState(false);
  const closeBetaModal = useCallback(() => setBetaModalOpen(false), []);

  /* ── Modal content config ── */
  const MODAL_CONTENT: Record<ModalKey, {
    title: string;
    videoSrc: string;
    videoAlt: string;
    mediaMock: 'phone' | 'browser';
    heading: string;
    body: string;
    bullets: string[];
    ctaLabel: string;
    ctaUrl: string;
    ctaAction?: () => void;
  }> = {
    mobile: {
      title: t('landing.modal_mobile_title', 'Mobile App'),
      videoSrc: cameraOcrVideo,
      videoAlt: t('landing.modal_mobile_video_alt', 'Camera OCR demo video'),
      mediaMock: 'phone',
      heading: t('landing.modal_mobile_h', 'Every printed page, instantly readable'),
      body: t('landing.modal_mobile_p1', 'You spot a handout, a menu, or a sign you need to read — and the wall of text feels impossible. You point your phone, and DysGo turns it into clean, comfortable text in seconds. The relief is immediate.'),
      bullets: Array.isArray(t('landing.modal_mobile_bullets', { returnObjects: true }))
        ? t('landing.modal_mobile_bullets', { returnObjects: true }) as string[]
        : ['Printed text becomes readable instantly', 'Alternating color stripes guide your eyes line by line', 'Text reformats to your comfort — no squinting', 'Works with handouts, menus, signs, and more'],
      ctaLabel: t('landing.modal_mobile_cta', 'Join the waitlist'),
      ctaUrl: t('landing.cta_mobile_url', ''),
      ctaAction: () => { setActiveModal(null); setBetaModalOpen(true); },
    },
    web: {
      title: t('landing.modal_web_title', 'Web App'),
      videoSrc: webappDemoVideo,
      videoAlt: t('landing.modal_web_video_alt', 'Web app demo video'),
      mediaMock: 'browser',
      heading: t('landing.modal_web_h', 'Long documents finally feel manageable'),
      body: t('landing.modal_web_p1', 'You sit down with a PDF that stretches on for pages, and just looking at it feels exhausting. You import it into DysGo, and the text reshapes into something you can actually read. You stay focused instead of losing your place.'),
      bullets: Array.isArray(t('landing.modal_web_bullets', { returnObjects: true }))
        ? t('landing.modal_web_bullets', { returnObjects: true }) as string[]
        : ['Any PDF loads ready to read — no setup needed', 'Alternating stripe backgrounds keep your place effortlessly', 'Adjust text size and spacing to your exact comfort', 'Turn complex notes into visual mind maps instantly'],
      ctaLabel: t('landing.modal_web_cta', 'Start reading free'),
      ctaUrl: t('landing.cta_web_url', '/app/auth/login'),
    },
    plugin: {
      title: t('landing.modal_plugin_title', 'Browser Plugin'),
      videoSrc: pluginDemoVideo,
      videoAlt: t('landing.modal_plugin_video_alt', 'Browser plugin demo video'),
      mediaMock: 'browser',
      heading: t('landing.modal_plugin_h', 'Any website, instantly comfortable'),
      body: t('landing.modal_plugin_p1', 'You land on a website and the text is dense, cramped, and hard to follow — especially when you have dyslexia. You hover, and DysGo shows it reformatted in a clean, easy-to-read popup right where you are. No copy-pasting, no leaving the page.'),
      bullets: Array.isArray(t('landing.modal_plugin_bullets', { returnObjects: true }))
        ? t('landing.modal_plugin_bullets', { returnObjects: true }) as string[]
        : ['Hover any text to see it beautifully reformatted', 'Alternating backgrounds ease reading on every site', 'Works on every website you visit — no exceptions', 'Nothing to copy, paste, or switch apps for'],
      ctaLabel: t('landing.modal_plugin_cta', 'See it live'),
      ctaUrl: t('landing.cta_plugin_url', '#plugin-strip-heading'),
    },
  };

  /* ── Feature 2: Live plugin demo ── */
  const [pluginDemoEnabled, setPluginDemoEnabled] = useState(false);

  // Cleanup on unmount only — script loading is deferred to when pluginDemoEnabled becomes true
  useEffect(() => {
    setDysGoDemoVisible(false);
    return cleanupPluginDOM;
  }, []);

  // Load script + init on enable, tear down on disable
  useEffect(() => {
    if (!pluginDemoEnabled) {
      disablePlugin();
      return;
    }
    ensureDysGoEmbedScriptLoaded();
    setDysGoDemoVisible(true);
    // Do NOT call enable() here — it is not yet wired on first load (the
    // contentScript IIFE that adds enable() to DysGoEmbed runs asynchronously
    // after init()). Instead, call enable() inside tryInitPlugin() only when
    // the script is already initialized (i.e. on a re-enable after a previous disable).
    if (tryInitPlugin()) return;
    const id = window.setInterval(() => {
      if (tryInitPlugin()) window.clearInterval(id);
    }, 50);
    return () => window.clearInterval(id);
  }, [pluginDemoEnabled]);

  /* ── Card media: hover-to-play with play/pause toggle ── */
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const webVideoRef = useRef<HTMLVideoElement>(null);
  const pluginVideoRef = useRef<HTMLVideoElement>(null);
  const [mobilePlaying, setMobilePlaying] = useState(false);
  const [webPlaying, setWebPlaying] = useState(false);
  const [pluginPlaying, setPluginPlaying] = useState(false);

  const cardVideoRef = (card: 'mobile' | 'web' | 'plugin') =>
    card === 'mobile' ? mobileVideoRef : card === 'web' ? webVideoRef : pluginVideoRef;

  const toggleCardPlay = (card: 'mobile' | 'web' | 'plugin') => {
    const v = cardVideoRef(card).current;
    if (!v) return;
    const setter = card === 'mobile' ? setMobilePlaying : card === 'web' ? setWebPlaying : setPluginPlaying;
    const playing = card === 'mobile' ? mobilePlaying : card === 'web' ? webPlaying : pluginPlaying;
    if (playing) { v.pause(); setter(false); }
    else { v.play().catch(() => {}); setter(true); }
  };

  const handleCardHover = (card: 'mobile' | 'web' | 'plugin', entering: boolean) => {
    const v = cardVideoRef(card).current;
    if (!v) return;
    const setter = card === 'mobile' ? setMobilePlaying : card === 'web' ? setWebPlaying : setPluginPlaying;
    if (entering) { v.play().catch(() => {}); setter(true); }
    else { v.pause(); setter(false); }
  };

  return (
    <div id="static-root" className="landing-page landing-funnel">
      <style>{`
        /* ── CSS Variables ── */
        :root {
          --lf-bg: #ffffff;
          --lf-card: #ffffff;
          --lf-text: #0b1220;
          --lf-muted: #5b6b84;
          --lf-border: rgba(15, 23, 42, 0.10);
          --lf-shadow: 0 18px 60px rgba(15, 23, 42, 0.16);
          --lf-shadow-sm: 0 12px 28px rgba(15, 23, 42, 0.10);
          --lf-accent: var(--primary);
          --lf-green: #2dce89;
          --lf-radius-xs: 8px;
          --lf-radius-sm: 12px;
          --lf-radius-md: 16px;
          --lf-radius-lg: 20px;
          --lf-radius-xl: 24px;
          --lf-radius-full: 999px;
          --lf-label-spacing: 0.06em;
        }

        /* Screen reader only utility */
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        .landing-funnel {
          background: var(--lf-bg);
          color: var(--lf-text);
        }
        .landing-funnel main {
          overflow-x: hidden;
        }

        /* Global pointer cursor for all clickable elements */
        .landing-funnel a,
        .landing-funnel button,
        .landing-funnel [role="button"],
        .landing-funnel [tabindex="0"],
        .landing-funnel input[type="checkbox"],
        .landing-funnel input[type="color"],
        .landing-funnel input[type="range"],
        .landing-funnel .lf-seg-btn,
        .landing-funnel .lf-theme-btn,
        .landing-funnel .lf-card-footer {
          cursor: pointer;
        }

        .lf-plugin-off #mtt-shadow-host,
        .lf-plugin-off #mttstyleHighlight,
        .lf-plugin-off #mttstyleSubtitle {
          display: none !important;
        }

        /* ── Hero ── */
        .lf-hero {
          position: relative;
          padding: 6rem 0 calc(7rem + 70px);
          background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%);
          border: none;
          /* clip-path creates the diagonal bottom edge directly on the
             hero element — no separate SVG separator needed, so there is
             zero boundary seam between hero and the next section. */
          clip-path: polygon(0 0, 100% 0, 100% calc(100% - 70px), 0 100%);
        }
        .lf-hero-content {
          position: relative;
          z-index: 5;
          max-width: 960px;
          margin: 0 auto;
          text-align: center;
        }
        .lf-hero .striped-bg-hero {
          padding: 0;
        }
        .lf-hero .lf-subtitle {
          color: rgba(255,255,255,0.95);
          max-width: 640px;
          margin: 0 auto 1.5rem;
          font-size: 1.2rem;
          line-height: 1.55;
        }
        .lf-hero .lf-kw {
          color: #fff;
          font-weight: 600;
          border-bottom: 2px solid rgba(45,206,137,0.7);
          vertical-align: middle;
          display: inline-block;
          line-height: inherit;
        }
        .lf-hero .lf-kw--spacing {
          letter-spacing: 0.25em;
          border-bottom: none;
        }
        .lf-hero .lf-kw--size {
          font-size: 1.35em;
          border-bottom: none;
          position: relative;
          top: -2px;
        }
        .lf-hero .lf-kw--rhythm {
          background: #B3D9FF;
          color: #000;
          padding: 0.1em 0.4em;
          border-radius: 4px;
          border-bottom: none;
        }
        .lf-hero .lf-cta {
          justify-content: center;
        }
        /* Inner content wrapper */
        .lf-dl-inner {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
        }
        .glow-btn .lf-dl-arrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3em;
          line-height: 1;
        }
        .glow-btn:hover .lf-dl-arrow {
          animation: lf-content-bounce 0.5s ease-in-out;
        }
        @keyframes lf-content-bounce {
          0%   { transform: translateY(0); }
          20%  { transform: translateY(-4px); }
          36%  { transform: translateY(0); }
          54%  { transform: translateY(-2.5px); }
          70%  { transform: translateY(0); }
          100% { transform: translateY(0); }
        }
        .lf-hero .lf-eyebrow {
          background: rgba(255,255,255,0.15);
          border-color: rgba(255,255,255,0.3);
          color: rgba(255,255,255,0.9);
        }

        /* hero diagonal is via clip-path — no separator CSS needed */

        /* ── Shared ── */
        .landing-funnel .punctuation {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.05em 0.45em;
          margin: 0 0.08em;
          border-radius: 999px;
          background: rgba(11, 18, 32, 0.92);
          color: #fff;
          transform: translateY(-0.06em);
        }
        .landing-funnel .punctuation-pill {
          background-color: black;
          color: white;
          border-radius: 12px;
          padding: 2px 8px;
          margin: 0 3px;
          display: inline;
          white-space: nowrap;
        }
        .lf-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.82rem;
          font-weight: 700;
          letter-spacing: var(--lf-label-spacing);
          text-transform: uppercase;
          color: var(--lf-muted);
          background: rgba(54, 102, 156, 0.10);
          border: 1px solid rgba(54, 102, 156, 0.18);
          border-radius: 999px;
          padding: 0.4rem 0.75rem;
          width: fit-content;
          margin: 0 auto 0.9rem;
        }
        .lf-cta {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
          margin: 1.25rem 0 0;
        }
        .lf-cta .btn {
          border-radius: 999px;
          font-weight: 700;
          letter-spacing: 0.02em;
        }
        .lf-section {
          padding: 5rem 0;
        }
        /* nth-of-type counts all <section> siblings: hero is 1st (odd),
           funnel is 2nd (even), etc.  Even = white keeps the first content
           section white — matching the SVG fill and the wrapper bg so no
           colour can bleed at the hero/section boundary. */
        .lf-section:nth-of-type(even) {
          background: #ffffff;
        }
        .lf-section:nth-of-type(odd) {
          background: #f8f9fb;
        }
        .lf-section-title {
          font-size: clamp(1.5rem, 2.2vw, 2.1rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          margin: 0 0 0.75rem;
        }
        .lf-section-subtitle {
          margin: 0 0 1.5rem;
          color: var(--lf-muted);
          font-size: 1.05rem;
          max-width: 72ch;
        }
        .lf-card {
          background: var(--lf-card);
          border: 1px solid var(--lf-border);
          border-radius: var(--lf-radius-lg);
          box-shadow: var(--lf-shadow-sm);
        }
        .lf-muted {
          color: var(--lf-muted);
        }

        /* ── Funnel Section ── */
        .lf-funnel {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
          max-width: 960px;
          margin: 0 auto;
        }
        .lf-funnel-step {
          width: 100%;
          text-align: center;
        }
        .lf-funnel-step-label {
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: var(--lf-label-spacing);
          text-transform: uppercase;
          color: var(--lf-muted);
          margin-bottom: 0.5rem;
        }
        .lf-funnel-step-title {
          font-size: 1.3rem;
          font-weight: 800;
          letter-spacing: -0.01em;
          margin: 0 0 1rem;
        }

        /* Funnel connector arrow — tighter spacing */
        .lf-funnel-arrow {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0.15rem 0;
        }
        .lf-funnel-arrow svg {
          display: block;
          color: rgba(54, 102, 156, 0.4);
        }

        /* Funnel icons row */
        .lf-funnel-icons {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 0.5rem;
        }
        .lf-funnel-icon-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.4rem;
          min-width: 100px;
        }
        .lf-funnel-icon-badge .lf-icon-circle {
          width: 52px;
          height: 52px;
          border-radius: var(--lf-radius-md);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(54, 102, 156, 0.10);
          border: 1px solid rgba(54, 102, 156, 0.18);
          color: var(--primary);
          font-size: 1.2rem;
        }
        .lf-funnel-icon-badge span:last-child {
          font-size: 0.82rem;
          font-weight: 600;
          color: var(--lf-muted);
        }

        /* Funnel core card (DysGoze) */
        .lf-funnel-core {
          max-width: 540px;
          margin: 0 auto;
          padding: 1.5rem;
        }
        .lf-funnel-core-subtitle {
          color: var(--lf-muted);
          font-size: 0.95rem;
          margin: 1rem 0 0;
          line-height: 1.5;
        }
        .lf-stripe-preview {
          border-radius: var(--lf-radius-md);
          overflow: hidden;
          border: 1px solid rgba(15, 23, 42, 0.10);
          background: #fff;
        }
        .lf-stripe-preview-line {
          padding: 6px 16px;
          font-size: 0.88rem;
          font-weight: 400;
          color: var(--lf-text);
          line-height: 1.7;
        }
        .lf-stripe-preview-line:nth-child(odd) {
          background: #E8F2FC;
        }
        .lf-stripe-preview-line:nth-child(even) {
          background: #FFFFFF;
        }

        /* Funnel branch connector — 90-degree angle arms */
        .lf-funnel-branch {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 0.35rem 0 0.25rem;
          position: relative;
        }
        .lf-funnel-branch-svg {
          display: block;
          width: 100%;
          max-width: 600px;
          height: 80px;
        }

        /* Funnel product cards — NO hover on outer card (fix #4) */
        .lf-funnel-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
          width: 100%;
        }
        .lf-funnel-card {
          padding: 1.5rem 1.5rem 0;
          text-align: left;
          display: flex;
          flex-direction: column;
        }
        .lf-funnel-card-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.6rem;
        }
        .lf-funnel-card-icon {
          width: 42px;
          height: 42px;
          border-radius: var(--lf-radius-sm);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(54, 102, 156, 0.10);
          border: 1px solid rgba(54, 102, 156, 0.18);
          color: var(--primary);
          font-size: 1.05rem;
          flex-shrink: 0;
        }
        .lf-funnel-card-titles {
          display: flex;
          flex-direction: column;
        }
        .lf-funnel-card-label {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: var(--lf-label-spacing);
          text-transform: uppercase;
          color: var(--lf-muted);
          line-height: 1.2;
        }
        .lf-funnel-card h3 {
          font-size: 1.1rem;
          font-weight: 800;
          margin: 0;
          line-height: 1.3;
        }
        .lf-funnel-card p {
          margin: 0 0 1rem;
          color: var(--lf-muted);
          line-height: 1.5;
          font-size: 0.92rem;
        }
        .lf-funnel-card-media {
          border-radius: var(--lf-radius-md);
          overflow: hidden;
          background: linear-gradient(145deg, #f0f4f8 0%, #e8eef6 100%);
          position: relative;
          flex: 1;
          min-height: 260px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: zoom-in;
        }

        /* Play/pause button — bottom-right corner */
        .lf-media-play-btn {
          position: absolute;
          bottom: 10px;
          right: 10px;
          z-index: 5;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          opacity: 1;
          transition: opacity 0.3s ease;
          pointer-events: auto;
          border: none;
          cursor: pointer;
          padding: 0;
        }
        .lf-media-play-btn .lf-play-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 10px rgba(15, 23, 42, 0.16);
          color: var(--primary);
          font-size: 0.8rem;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        /* When playing: hide button */
        .lf-funnel-card-media.is-playing .lf-media-play-btn {
          opacity: 0;
        }
        /* Hover when playing: show pause */
        .lf-funnel-card-media.is-playing:hover .lf-media-play-btn {
          opacity: 1;
        }
        .lf-media-play-btn:hover .lf-play-icon {
          transform: scale(1.12);
          box-shadow: 0 4px 16px rgba(15, 23, 42, 0.22);
        }

        /* Card footer — full-width, breaks out of card padding */
        .lf-card-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          padding: 0.75rem 1rem;
          margin: 1rem -1.5rem 0;
          width: calc(100% + 3rem);
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: var(--lf-label-spacing);
          text-transform: uppercase;
          color: var(--primary);
          border-top: 1px solid rgba(15, 23, 42, 0.06);
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease;
          background: transparent;
          border-radius: 0 0 var(--lf-radius-lg) var(--lf-radius-lg);
        }
        .lf-card-footer:hover {
          background: rgba(54, 102, 156, 0.04);
          color: var(--lf-text);
        }
        .lf-card-footer i {
          font-size: 0.72rem;
          transition: transform 0.2s ease;
        }
        .lf-card-footer:hover i {
          transform: translateX(2px);
        }

        /* Phone mockup */
        .lf-phone-mock {
          border-radius: 22px;
          border: 3px solid #1a1a2e;
          background: #1a1a2e;
          box-shadow: 0 8px 32px rgba(15, 23, 42, 0.18), inset 0 0 0 1px rgba(255,255,255,0.06);
          overflow: hidden;
          padding: 3px;
          max-width: 160px;
          margin: 1rem auto;
          position: relative;
        }
        .lf-phone-mock::before {
          content: '';
          position: absolute;
          top: 6px;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 5px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.12);
          z-index: 2;
        }
        .lf-phone-mock video {
          width: 100%;
          height: 220px;
          border-radius: 18px;
          display: block;
          object-fit: cover;
        }

        /* Browser mockup */
        .lf-browser-mock {
          border-radius: var(--lf-radius-sm);
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #fff;
          box-shadow: 0 4px 20px rgba(15, 23, 42, 0.10);
          overflow: hidden;
          margin: 0.75rem;
          width: calc(100% - 1.5rem);
        }
        .lf-browser-bar {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 6px 10px;
          border-bottom: 1px solid rgba(15, 23, 42, 0.06);
          background: #f8f9fb;
        }
        .lf-browser-bar .dot {
          width: 6px;
          height: 6px;
          border-radius: 999px;
          display: inline-block;
        }
        .lf-browser-bar .dot.red { background: #FF5F57; }
        .lf-browser-bar .dot.yellow { background: #FEBC2E; }
        .lf-browser-bar .dot.green { background: #28C840; }
        .lf-browser-bar .addr {
          height: 8px;
          flex: 1 1 auto;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.05);
          margin-left: 6px;
        }
        .lf-browser-body {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3px;
          padding: 3px;
        }
        .lf-browser-body img {
          width: 100%;
          height: 100px;
          object-fit: cover;
          border-radius: 4px;
          display: block;
        }

        /* Plugin illustration */
        .lf-plugin-illus {
          padding: 1rem 0.85rem;
          min-height: 180px;
          position: relative;
          width: 100%;
        }
        .lf-plugin-illus-line {
          margin: 0 0 0.55rem;
          font-size: 0.75rem;
          font-weight: 400;
          color: var(--lf-muted);
          line-height: 1.5;
          padding: 3px 5px;
          border-radius: 4px;
          transition: background 0.3s ease, color 0.3s ease;
        }
        /* Second line is "highlighted" by default to show the effect */
        .lf-plugin-illus .lf-plugin-illus-line:nth-child(2) {
          background: rgba(54, 102, 156, 0.07);
          color: var(--lf-text);
        }
        .lf-plugin-illus-pop {
          position: absolute;
          right: 8px;
          top: 28%;
          width: 110px;
          border-radius: 10px;
          border: 1px solid rgba(54, 102, 156, 0.18);
          background: #fff;
          box-shadow: 0 6px 24px rgba(15, 23, 42, 0.12);
          padding: 0.5rem;
          text-align: left;
          opacity: 0.85;
          transition: opacity 0.3s ease, transform 0.3s ease;
        }
        .lf-plugin-illus-pop b {
          display: block;
          font-size: 0.68rem;
          font-weight: 800;
          margin-bottom: 0.12rem;
          color: var(--primary);
        }
        .lf-plugin-illus-pop span {
          display: block;
          font-size: 0.64rem;
          color: var(--lf-muted);
          font-weight: 600;
          line-height: 1.3;
        }

        /* ── Web card slideshow animation ── */
        .lf-browser-body.lf-slideshow {
          position: relative;
          overflow: hidden;
          grid-template-columns: 1fr;
          height: 200px;
        }
        .lf-browser-body.lf-slideshow img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 0;
          opacity: 0;
          animation: lf-slideshow 8s ease-in-out infinite;
        }
        .lf-browser-body.lf-slideshow img:nth-child(1) { animation-delay: 0s; }
        .lf-browser-body.lf-slideshow img:nth-child(2) { animation-delay: 2s; }
        .lf-browser-body.lf-slideshow img:nth-child(3) { animation-delay: 4s; }
        .lf-browser-body.lf-slideshow img:nth-child(4) { animation-delay: 6s; }
        @keyframes lf-slideshow {
          0%   { opacity: 0; }
          5%   { opacity: 1; }
          25%  { opacity: 1; }
          30%  { opacity: 0; }
          100% { opacity: 0; }
        }

        /* ── Plugin card active/playing animation ── */
        .lf-plugin-illus.lf-plugin-active .lf-plugin-illus-pop {
          opacity: 1;
          transform: scale(1.04);
          box-shadow: 0 8px 32px rgba(15, 23, 42, 0.18);
        }
        .lf-plugin-illus.lf-plugin-active .lf-plugin-illus-line:nth-child(2) {
          background: rgba(54, 102, 156, 0.12);
          color: var(--lf-text);
          font-weight: 500;
        }

        /* ── Playground / Demo ── */
        .lf-playground {
          display: grid;
          grid-template-columns: 0.95fr 1.05fr;
          gap: 0.75rem;
          align-items: start;
        }
        .lf-textarea {
          width: 100%;
          min-height: 240px;
          resize: vertical;
          border-radius: var(--lf-radius-md);
          border: 1px solid rgba(15, 23, 42, 0.14);
          padding: 0.9rem 1rem;
          font-size: 1rem;
          line-height: 1.5;
          outline: none;
          background: rgba(255,255,255,0.9);
        }
        .lf-textarea:focus {
          border-color: rgba(54, 102, 156, 0.45);
          box-shadow: 0 0 0 4px rgba(54, 102, 156, 0.12);
        }
        .lf-preview-scroll {
          height: 320px;
          overflow: hidden auto;
          border-radius: var(--lf-radius-md);
          border: 1px solid rgba(15, 23, 42, 0.14);
          background: rgba(255,255,255,0.95);
          scrollbar-gutter: stable;
        }

        /* ── App Frame (new playground) ── */
        .lf-app-frame {
          border-radius: var(--lf-radius-lg);
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #fff;
          box-shadow: 0 20px 60px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.04);
          overflow: hidden;
          position: relative;
        }
        .lf-app-chrome {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: #f8f9fb;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
        }
        .lf-app-chrome .dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          display: inline-block;
        }
        .lf-app-chrome .dot.red { background: #FF5F57; }
        .lf-app-chrome .dot.yellow { background: #FEBC2E; }
        .lf-app-chrome .dot.green { background: #28C840; }
        .lf-app-chrome-url {
          margin-left: 12px;
          padding: 4px 16px;
          border-radius: 6px;
          background: rgba(15, 23, 42, 0.04);
          font-size: 0.78rem;
          font-weight: 500;
          color: var(--lf-muted);
          flex: 1;
        }

        .lf-app-toolbar {
          padding: 0.5rem 1rem;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          background: #fbfcfd;
        }
        .lf-app-toolbar .lf-controls {
          margin-top: 0;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.6rem;
        }
        .lf-app-toolbar .lf-seg {
          margin: 0;
        }
        .lf-app-toolbar .lf-theme-row {
          margin: 0;
          gap: 0.35rem;
        }
        .lf-app-toolbar .lf-grid {
          display: flex;
          gap: 0.75rem;
          margin: 0;
        }
        .lf-app-toolbar .lf-field {
          grid-template-columns: auto 1fr;
          min-width: 140px;
        }
        .lf-app-toolbar .lf-field-label {
          font-size: 0.76rem;
        }
        /* Hide color pickers and punctuation in toolbar for cleaner look */
        .lf-app-toolbar .lf-color-row,
        .lf-app-toolbar .lf-toggle {
          display: none;
        }

        .lf-app-body {
          display: flex;
          flex-direction: column;
          min-height: 380px;
        }
        .lf-app-pane {
          display: flex;
          flex-direction: column;
        }
        .lf-app-pane-label {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.6rem 1rem;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: var(--lf-label-spacing);
          text-transform: uppercase;
          color: var(--lf-muted);
          border-bottom: 1px solid rgba(15, 23, 42, 0.06);
        }
        .lf-app-pane-label i {
          font-size: 0.72rem;
        }
        .lf-app-pane-actions {
          padding: 0.5rem 1rem;
          border-top: 1px solid rgba(15, 23, 42, 0.06);
        }
        .lf-app-btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          border: none;
          background: none;
          padding: 0.3rem 0.6rem;
          border-radius: 6px;
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--lf-muted);
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .lf-app-btn-ghost:hover {
          background: rgba(15, 23, 42, 0.05);
          color: var(--lf-text);
        }
        .lf-app-btn-ghost i {
          font-size: 0.7rem;
        }
        .lf-app-reading-scroll {
          flex: 1;
          overflow-y: auto;
          scrollbar-gutter: stable;
          max-height: 360px;
        }
        .lf-app-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.6rem 1rem;
          border-top: 1px solid rgba(15, 23, 42, 0.08);
          background: #fbfcfd;
          font-size: 0.78rem;
          color: var(--lf-muted);
        }
        .lf-app-footer-cta {
          display: inline-flex;
          align-items: center;
          font-weight: 700;
          color: var(--primary);
          text-decoration: none;
          font-size: 0.78rem;
          letter-spacing: var(--lf-label-spacing);
          text-transform: uppercase;
          transition: opacity 0.15s ease;
        }
        .lf-app-footer-cta:hover {
          opacity: 0.75;
        }

        /* Controls */
        .lf-controls {
          margin-top: 0.8rem;
        }
        .lf-seg {
          display: inline-flex;
          gap: 0.4rem;
          padding: 0.25rem;
          border-radius: 999px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: rgba(255,255,255,0.75);
        }
        .lf-seg-btn {
          border: 0;
          background: transparent;
          border-radius: 999px;
          padding: 0.42rem 0.75rem;
          font-weight: 700;
          font-size: 0.88rem;
          color: var(--lf-muted);
          cursor: pointer;
        }
        .lf-seg-btn:hover {
          color: var(--lf-text);
        }
        .lf-seg-btn.active {
          background: var(--primary);
          color: #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
        }
        .lf-theme-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.45rem;
          margin-top: 0.75rem;
          margin-bottom: 0.15rem;
        }
        .lf-theme-btn {
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: rgba(255, 255, 255, 0.9);
          border-radius: 999px;
          padding: 0.34rem 0.55rem;
          font-weight: 700;
          font-size: 0.88rem;
          color: var(--lf-text);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
        }
        .lf-theme-btn.active {
          border-color: rgba(54, 102, 156, 0.35);
          box-shadow: 0 0 0 4px rgba(54, 102, 156, 0.10);
        }
        .lf-swatch {
          width: 22px;
          height: 14px;
          border-radius: var(--lf-radius-xs);
          border: 1px solid rgba(15, 23, 42, 0.12);
          display: inline-block;
        }
        .lf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.6rem;
          margin-top: 0.75rem;
        }
        .lf-field {
          display: grid;
          grid-template-columns: 120px 1fr;
          gap: 0.75rem;
          align-items: center;
          margin: 0;
        }
        .lf-field-label {
          color: var(--lf-muted);
          font-weight: 700;
          font-size: 0.86rem;
          letter-spacing: var(--lf-label-spacing);
        }
        .lf-field input[type="range"] {
          width: 100%;
        }
        .lf-color-row {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.75rem;
        }
        .lf-color-field {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .lf-color-field .lf-field-label {
          white-space: nowrap;
        }
        .lf-color-swatch {
          width: 36px;
          height: 36px;
          border-radius: var(--lf-radius-xs);
          border: 2px solid var(--lf-border);
          cursor: pointer;
          position: relative;
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.85);
          transition: box-shadow 0.15s ease;
          flex-shrink: 0;
        }
        .lf-color-swatch:hover {
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.85), 0 0 0 3px rgba(54, 102, 156, 0.15);
        }
        .lf-color-swatch input[type="color"] {
          position: absolute;
          inset: 0;
          opacity: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
          border: none;
        }
        .lf-toggle {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.75rem;
          color: var(--lf-muted);
          font-weight: 700;
          user-select: none;
        }

        /* ── Testimonial Chat ── */
        .lf-chat {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          max-width: 680px;
          margin: 0 auto;
        }
        .lf-chat-row {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }
        .lf-chat-row.lf-chat-right {
          flex-direction: row-reverse;
        }
        .lf-chat-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        }
        .lf-chat-avatar-logo {
          border-radius: 0;
          object-fit: contain;
        }
        .lf-chat-bubble {
          position: relative;
          border-radius: 18px;
          padding: 1rem 1.25rem;
          max-width: 85%;
          line-height: 1.55;
          font-size: 0.95rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }
        /* Desktop: single-line quote */
        .lf-quote-desktop { display: block; }
        .lf-quote-mobile { display: none; }
        /* DysGo bubble — gradient blue */
        .lf-chat-bubble-dysgo {
          background: linear-gradient(135deg, var(--primary), #1B5694);
          color: #fff;
          font-weight: 800;
          font-size: 1.05rem;
          letter-spacing: -0.01em;
        }

        /* ── Hint Pill (plugin demo) ── */
        .lf-hint-pill {
          display: inline-flex;
          align-items: center;
          background: rgba(54, 102, 156, 0.08);
          border: 1px solid rgba(54, 102, 156, 0.18);
          border-radius: 999px;
          padding: 0.4rem 0.85rem;
          color: var(--primary);
          font-weight: 700;
          font-size: 0.82rem;
          animation: lf-hint-pulse 2s ease-in-out infinite;
        }
        @keyframes lf-hint-pulse {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.02); }
        }

        /* ── About / Mission ── */
        .lf-about-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: center;
          max-width: 960px;
          margin: 0 auto;
        }
        .lf-about-text .lf-eyebrow {
          margin: 0 0 0.9rem;
        }
        .lf-about-text h2 {
          font-size: clamp(1.5rem, 2.2vw, 2.1rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          margin: 0 0 1rem;
        }
        .lf-about-text p {
          color: var(--lf-muted);
          font-size: 1.02rem;
          line-height: 1.6;
          margin: 0 0 1.5rem;
        }
        .lf-about-img-wrap {
          aspect-ratio: 4 / 5;
          max-width: 380px;
          justify-self: center;
        }
        .lf-about-img-wrap img {
          border-radius: inherit;
        }

        /* ── Download Band ── */
        .lf-band {
          background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%) !important;
          color: #fff;
          border-top: 1px solid rgba(255,255,255,0.12);
          border-bottom: 1px solid rgba(255,255,255,0.12);
        }
        .lf-download {
          text-align: center;
          padding: 3.25rem 0;
        }
        .lf-download .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 56px;
          border-radius: 999px;
          box-shadow: none;
        }
        .lf-download .btn img {
          display: block;
        }
        /* Fix: download band outline-white hover matches hero style */
        .lf-download .btn-outline-white {
          color: #fff;
          border: 2px solid rgba(255,255,255,0.7);
          background: transparent;
        }
        .lf-download .btn-outline-white:hover,
        .lf-download .btn-outline-white:focus {
          background: rgba(255,255,255,0.15);
          color: #fff;
          border-color: rgba(255,255,255,0.85);
        }

        /* ── Glow CTA button ── */
        .glow-btn {
          --h: 56px;
          --px: 2.5rem;
          --radius: 999px;
          --ring: 2px;
          --blue-1: rgba(47,107,255,.7);
          --blue-2: rgba(34,211,255,.7);
          --blue-3: rgba(45,206,137,.6);
          position: relative;
          display: inline-grid;
          place-items: center;
          height: var(--h);
          padding: 0;
          border-radius: var(--radius);
          border: 0;
          cursor: pointer;
          font-size: 1.1rem;
          font-weight: 800;
          letter-spacing: 0.02em;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transform: translateZ(0);
          outline: none;
          text-decoration: none;
          --glow-opacity: 0;
          --glow-blur: 16px;
          --ring-opacity: 0;
          background: transparent;
          color: var(--primary);
          box-shadow: none;
          transition: box-shadow 250ms ease;
        }
        .glow-btn::before {
          content: '';
          position: absolute;
          inset: -10px;
          border-radius: calc(var(--radius) + 12px);
          background: linear-gradient(
            135deg,
            var(--blue-1),
            var(--blue-2),
            var(--blue-3),
            var(--blue-2),
            var(--blue-1)
          );
          filter: blur(var(--glow-blur));
          opacity: 0;
          z-index: -2;
          transition: opacity 250ms ease, filter 250ms ease, transform 250ms ease;
        }
        .glow-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: var(--radius);
          padding: var(--ring);
          background: linear-gradient(
            135deg,
            rgba(47,107,255,.95),
            rgba(34,211,255,.95),
            rgba(45,206,137,.85),
            rgba(34,211,255,.95),
            rgba(47,107,255,.95)
          );
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          z-index: -1;
          opacity: 0;
          transition: opacity 250ms ease, transform 250ms ease;
        }
        .glow-btn:hover {
          color: var(--primary);
          text-decoration: none;
          background: transparent;
          box-shadow: 0 0 24px rgba(255,255,255,0.5);
        }
        .glow-btn:hover::before {
          opacity: .55;
          filter: blur(18px);
          transform: translateY(-1px);
        }
        .glow-btn:hover::after {
          opacity: .9;
        }
        .glow-btn:active {
          transform: translateY(1px);
        }
        .glow-btn:active::before {
          opacity: calc(var(--glow-opacity) - .08);
          filter: blur(calc(var(--glow-blur) - 3px));
        }
        .glow-btn:active::after {
          opacity: calc(var(--ring-opacity) - .12);
        }
        .glow-btn:focus-visible::before {
          opacity: 0.78;
          filter: blur(26px);
        }
        .glow-btn:focus-visible::after {
          opacity: 1;
        }
        .glow-btn .lf-dl-inner {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          background: rgba(255,255,255,0.93);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          border-radius: calc(var(--radius) - 2px);
          margin: 2px;
          padding: 0 calc(var(--px) - 2px);
          height: calc(100% - 4px);
        }

        /* ── Feature 1: Before/After Playground ── */
        .lf-playground-input {
          margin-bottom: 0.75rem;
        }
        .lf-playground-input .lf-textarea {
          min-height: 140px;
        }
        .lf-pane-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 800;
          font-size: 0.88rem;
          letter-spacing: var(--lf-label-spacing);
          margin-bottom: 0.6rem;
          color: var(--lf-text);
        }
        .lf-pane-header i {
          font-size: 0.82rem;
        }
        .lf-pane-without .lf-pane-header {
          color: var(--lf-muted);
        }
        .lf-pane-with .lf-pane-header {
          color: var(--primary);
        }
        /* Fix: plain text uses system font to match "With" pane */
        .lf-plain-text {
          font-family: var(--bs-body-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif);
          font-size: 0.92rem;
          line-height: 1.25;
          color: #333;
          padding: 0.9rem 1rem;
          white-space: pre-wrap;
          word-break: break-word;
          min-height: 240px;
        }
        .lf-pane-with {
          position: relative;
        }

        /* Drop zone overlay */
        .lf-drop-overlay {
          position: absolute;
          inset: 0;
          z-index: 20;
          border-radius: var(--lf-radius-lg);
          border: 2.5px dashed rgba(54, 102, 156, 0.5);
          background: rgba(54, 102, 156, 0.06);
          backdrop-filter: blur(4px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s ease;
        }
        .lf-drop-active .lf-drop-overlay {
          opacity: 1;
          pointer-events: auto;
        }
        .lf-drop-icon {
          font-size: 2rem;
          color: var(--primary);
          animation: lf-drop-pulse 1.5s ease-in-out infinite;
        }
        .lf-drop-label {
          font-weight: 800;
          font-size: 1rem;
          color: var(--primary);
        }
        .lf-drop-hint {
          font-size: 0.82rem;
          color: var(--lf-muted);
          font-weight: 600;
        }
        @keyframes lf-drop-pulse {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50% { transform: scale(1.12); opacity: 1; }
        }

        /* Drop toast notification */
        .lf-drop-toast {
          position: fixed;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2147483001;
          background: var(--lf-text);
          color: #fff;
          padding: 0.75rem 1.25rem;
          border-radius: 999px;
          font-weight: 700;
          font-size: 0.92rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
          animation: lf-toast-in 0.3s ease;
        }
        @keyframes lf-toast-in {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        /* ── Feature 2: Plugin Demo Strip ── */
        .lf-plugin-strip {
          max-width: 720px;
          margin: 0 auto;
          text-align: center;
          padding: 2.5rem 0 0;
        }
        .lf-plugin-strip-body {
          position: relative;
          text-align: left;
          font-size: 1.02rem;
          line-height: 1.75;
          color: var(--lf-text);
          padding: 1.25rem 1.5rem;
          border-radius: var(--lf-radius-lg);
          border: 1px solid var(--lf-border);
          background: var(--lf-card);
          box-shadow: var(--lf-shadow-sm);
        }
        .lf-plugin-strip-hint {
          margin-top: 0.75rem;
          font-size: 0.82rem;
          color: var(--lf-muted);
          font-weight: 600;
        }

        /* ── Feature 3: Demo Modal ── */
        .lf-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(11, 18, 32, 0.52);
          backdrop-filter: blur(4px);
          z-index: 2147483000;
          display: grid;
          place-items: center;
          padding: 1.25rem;
          animation: lf-modal-fade-in 0.25s ease;
        }
        .lf-modal {
          width: min(960px, 100%);
          max-height: 90vh;
          overflow-y: auto;
          border-radius: 22px;
          border: 1px solid rgba(255,255,255,0.18);
          background: rgba(255,255,255,0.97);
          box-shadow: 0 30px 80px rgba(15, 23, 42, 0.35);
          animation: lf-modal-slide-up 0.3s ease;
        }
        .lf-beta-modal {
          width: min(480px, 100%);
        }
        .lf-modal-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid rgba(15, 23, 42, 0.10);
          background: rgba(255,255,255,0.98);
          position: sticky;
          top: 0;
          z-index: 5;
        }
        .lf-modal-head b {
          font-weight: 900;
          letter-spacing: -0.01em;
          font-size: 1.1rem;
        }
        .lf-modal-close {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: rgba(255,255,255,0.92);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--lf-text);
          font-size: 1.1rem;
          transition: background 0.15s ease;
        }
        .lf-modal-close:hover {
          background: rgba(15, 23, 42, 0.06);
        }
        .lf-modal-body {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 1.25rem;
          padding: 1.25rem;
        }
        .lf-modal-media {
          border-radius: var(--lf-radius-md);
          overflow: hidden;
          background: linear-gradient(145deg, #f0f4f8 0%, #e8eef6 100%);
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          max-height: 70vh;
        }
        .lf-modal-media .lf-media-play-btn {
          pointer-events: auto;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .lf-modal-media:hover .lf-media-play-btn {
          opacity: 1;
        }
        .lf-modal-media:not(.is-playing) .lf-media-play-btn {
          opacity: 1;
        }
        .lf-modal-media video {
          width: 100%;
          display: block;
        }
        .lf-modal-media img {
          width: 100%;
          display: block;
        }
        /* Phone mock inside modal — larger than card preview */
        .lf-modal-media .lf-phone-mock {
          max-width: 280px;
          margin: 2rem auto;
        }
        .lf-modal-media .lf-phone-mock video {
          height: auto;
          max-height: 50vh;
        }
        /* Browser mock inside modal */
        .lf-modal-media .lf-browser-mock {
          margin: 1rem;
          width: calc(100% - 2rem);
        }
        .lf-modal-media .lf-browser-mock video {
          max-height: 50vh;
          object-fit: contain;
        }
        .lf-modal-media-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem;
          padding: 0.5rem;
        }
        .lf-modal-media-grid img {
          width: 100%;
          height: 180px;
          object-fit: cover;
          border-radius: var(--lf-radius-xs);
          border: 1px solid rgba(15, 23, 42, 0.08);
        }
        .lf-modal-explain {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .lf-modal-explain h4 {
          font-size: 1rem;
          font-weight: 800;
          margin: 0;
        }
        .lf-modal-explain p {
          margin: 0;
          color: var(--lf-muted);
          font-size: 0.92rem;
          line-height: 1.55;
        }
        @keyframes lf-modal-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes lf-modal-slide-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Focus-visible for all interactive elements */
        .lf-funnel-card-media:focus-visible,
        .lf-seg-btn:focus-visible,
        .lf-theme-btn:focus-visible,
        .lf-card-footer:focus-visible,
        .lf-media-play-btn:focus-visible,
        .lf-app-btn-ghost:focus-visible,
        .lf-plugin-btn:focus-visible,
        .lf-modal-close:focus-visible {
          outline: 2px solid var(--primary);
          outline-offset: 2px;
        }
        .lf-seg-btn:focus-visible {
          outline-offset: -1px;
          border-radius: 999px;
        }

        /* ── Responsive ── */
        @media (max-width: 991.98px) {
          .lf-hero {
            padding: 5rem 0 4rem;
            clip-path: polygon(0 0, 100% 0, 100% calc(100% - 28px), 0 100%);
          }
          .lf-hero .striped-bg-hero {
            font-size: 1.9em;
          }
          .lf-hero .lf-subtitle {
            font-size: 1rem;
          }
          .lf-funnel-cards {
            grid-template-columns: 1fr;
            max-width: 400px;
            margin: 0 auto;
          }
          .lf-funnel-branch {
            display: none;
          }
          .lf-playground {
            grid-template-columns: 1fr;
          }
          .lf-chat-bubble {
            max-width: 90%;
          }
          .lf-quote-desktop { display: none; }
          .lf-quote-mobile { display: block; }
          .lf-about-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          .lf-about-img-wrap {
            max-width: 100%;
          }
          .lf-about-img-wrap img {
            width: 100%;
            height: auto;
          }
          .lf-cta {
            justify-content: center;
          }
          .lf-hero .glow-btn {
            width: 100%;
            justify-content: center;
            font-size: 1.05rem;
            --h: 56px;
            padding: 0;
          }
          .lf-field {
            grid-template-columns: 1fr;
            gap: 0.35rem;
          }
          .lf-app-toolbar .lf-controls {
            flex-direction: column;
            align-items: stretch;
          }
          .lf-app-toolbar .lf-seg {
            align-self: flex-start;
          }
          .lf-app-toolbar .lf-grid {
            display: grid;
            grid-template-columns: 1fr;
          }
          .lf-modal {
            width: 100%;
            max-height: 100vh;
            border-radius: 0;
          }
          .lf-modal-body {
            grid-template-columns: 1fr;
          }
          .lf-modal-backdrop {
            padding: 0;
          }
          .lf-plugin-section {
            display: none;
          }
          .lf-plugin-section + .lf-section {
            padding-top: 2rem;
          }
          .lf-app-body {
            min-height: auto;
          }
          .lf-app-reading-scroll {
            min-height: 280px;
            padding: 0.5rem;
          }
          .lf-app-reading-scroll > div {
            background-origin: padding-box !important;
            background-clip: padding-box !important;
            padding-left: 28px !important;
            padding-right: 28px !important;
          }
          .lf-app-footer {
            flex-direction: column;
            gap: 0.5rem;
            text-align: center;
          }
        }

        @media (max-width: 576px) {
          .lf-hero .striped-bg-hero {
            font-size: 1.8em;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .lf-modal-backdrop {
            animation: none;
          }
          .lf-modal {
            animation: none;
          }
          .lf-drop-icon {
            animation: none;
          }
          .lf-drop-toast {
            animation: none;
          }
        }
      `}</style>
      <a href="#main-content" className="sr-only" style={{ position: 'absolute', top: '-40px', left: 0, zIndex: 9999, background: '#fff', padding: '8px 16px' }} onFocus={(e) => { (e.target as HTMLElement).style.top = '0'; }} onBlur={(e) => { (e.target as HTMLElement).style.top = '-40px'; }}>
        Skip to main content
      </a>
      <PublicNavbar />
      <ConfigProvider initial={stripedInitialConfig} persist={false} storageKey="landing-funnel-playground">
      <main role="main" id="main-content">
        {/* ═══════════ 1. Hero ═══════════ */}
        <section className="lf-hero" id="hero" aria-labelledby="hero-heading">
          <div className="container">
            <div className="lf-hero-content">
              <div className="lf-eyebrow">
                <span aria-hidden="true" style={{ color: 'var(--lf-green)' }}>●</span>
                {t('landing.eyebrow', 'Reading, reimagined')}
              </div>

              <div className="striped-bg-hero mb-4">
                <h1 id="hero-heading" className="striped-bg-hero-text">
                  {renderPunctuated(t('landing.hero_title', 'Read with confidence, anywhere.'))}
                </h1>
              </div>

              <p className="lf-subtitle">
                <Trans
                  i18nKey="landing.funnel_subtitle_styled"
                  defaults="DysGo makes any text <kw>dyslexia-friendly</kw>. Adjust <spacing>spacing</spacing>, <size>size</size>, and <rhythm>rhythm</rhythm> — on your phone, your browser, or any website."
                  components={{
                    kw: <span className="lf-kw" />,
                    spacing: <span className="lf-kw lf-kw--spacing" />,
                    size: <span className="lf-kw lf-kw--size" />,
                    rhythm: <span className="lf-kw lf-kw--rhythm" />,
                  }}
                />
              </p>

              <nav className="lf-cta" style={{ justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }} aria-label="Primary actions">
                <a href="/app/auth/login" className="glow-btn">
                  <span className="lf-dl-inner">
                    <span className="lf-dl-arrow" aria-hidden="true">
                      <i className="fas fa-arrow-right" />
                    </span>
                    {t('landing.cta_get_started_free', 'Try DysGo Free')}
                  </span>
                </a>
                <a
                  href="#demo"
                  style={{
                    color: 'rgba(255,255,255,0.92)',
                    fontSize: '0.9rem',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                  }}
                >
                  {t('landing.hero_try_link', 'or try it now \u2014 no sign-up')}
                </a>
              </nav>
            </div>
          </div>

          {/* No SVG separator needed — clip-path on .lf-hero creates the diagonal */}
        </section>

        {/* ═══════════ 2. The Funnel ═══════════ */}
        <section className="lf-section" id="funnel" aria-labelledby="funnel-heading">
          <div className="container" style={{ textAlign: 'center' }}>
            <div className="lf-eyebrow" style={{ margin: '0 auto 1rem' }}>
              <span aria-hidden="true" style={{ color: 'var(--primary)' }}>
                <i className="fas fa-layer-group" style={{ fontSize: '0.72rem' }} />
              </span>
              {t('landing.funnel_eyebrow', 'Product suite')}
            </div>
            <h2 id="funnel-heading" className="lf-section-title">
              {t('landing.funnel_title', 'One suite. Three ways to read.')}
            </h2>
            <p className="lf-section-subtitle" style={{ margin: '0 auto 3rem' }}>
              {t('landing.funnel_subtitle', 'Snap a photo, import a PDF, paste text, or browse any website — DysGo adjusts spacing, size, and rhythm so you can read comfortably everywhere.')}
            </p>

            <div className="lf-funnel">
              {/* Three product cards — directly, no steps */}
              <div className="lf-funnel-cards" role="list" aria-label="Products">
                {/* Mobile App */}
                <div className="lf-card lf-funnel-card" role="listitem">
                  <div className="lf-funnel-card-header">
                    <div className="lf-funnel-card-icon" aria-hidden="true">
                      <i className="fas fa-mobile-screen-button" />
                    </div>
                    <div className="lf-funnel-card-titles">
                      <div className="lf-funnel-card-label">{t('landing.funnel_card_mobile_label', 'On the go')}</div>
                      <h3>{t('landing.funnel_card_mobile_title', 'Mobile App')}</h3>
                    </div>
                  </div>
                  <p>
                    {t(
                      'landing.funnel_card_mobile_desc',
                      'Snap worksheets and book pages with your camera. Read comfortably anywhere — bus, couch, classroom.',
                    )}
                  </p>
                  <div
                    className={`lf-funnel-card-media${mobilePlaying ? ' is-playing' : ''}`}
                    onMouseEnter={() => handleCardHover('mobile', true)}
                    onMouseLeave={() => handleCardHover('mobile', false)}
                    onClick={() => setActiveModal('mobile')}
                  >
                    <div className="lf-phone-mock">
                      <video
                        ref={mobileVideoRef}
                        src={cameraOcrVideo}
                        poster={cameraOcrPoster}
                        loop
                        muted
                        playsInline
                        preload="none"
                        aria-label={t('landing.funnel_mobile_video_alt', 'Camera OCR demo')}
                      />
                    </div>
                    <button
                      type="button"
                      className="lf-media-play-btn"
                      onClick={(e) => { e.stopPropagation(); toggleCardPlay('mobile'); }}
                      aria-label={mobilePlaying ? 'Pause demo' : 'Play demo'}
                    >
                      <span className="lf-play-icon">
                        <i className={`fas fa-${mobilePlaying ? 'pause' : 'play'}`} />
                      </span>
                    </button>
                  </div>
                  <div
                    className="lf-card-footer"
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveModal('mobile')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveModal('mobile'); } }}
                  >
                    <i className="fas fa-expand-alt" aria-hidden="true" />
                    {t('landing.view_demo', 'View demo')}
                  </div>
                </div>

                {/* Web App */}
                <div className="lf-card lf-funnel-card" role="listitem">
                  <div className="lf-funnel-card-header">
                    <div className="lf-funnel-card-icon" aria-hidden="true">
                      <i className="fas fa-display" />
                    </div>
                    <div className="lf-funnel-card-titles">
                      <div className="lf-funnel-card-label">{t('landing.funnel_card_web_label', 'Deep work')}</div>
                      <h3>{t('landing.funnel_card_web_title', 'Web App')}</h3>
                    </div>
                  </div>
                  <p>
                    {t(
                      'landing.funnel_card_web_desc',
                      'Import PDFs, read in split view, and build mind maps. Your full reading workspace.',
                    )}
                  </p>
                  <div
                    className={`lf-funnel-card-media${webPlaying ? ' is-playing' : ''}`}
                    onMouseEnter={() => handleCardHover('web', true)}
                    onMouseLeave={() => handleCardHover('web', false)}
                    onClick={() => setActiveModal('web')}
                  >
                    <div className="lf-browser-mock">
                      <div className="lf-browser-bar" aria-hidden="true">
                        <span className="dot red" />
                        <span className="dot yellow" />
                        <span className="dot green" />
                        <span className="addr" />
                      </div>
                      <video
                        ref={webVideoRef}
                        src={webappDemoVideoSm}
                        poster={webappDemoSmPoster}
                        loop
                        muted
                        playsInline
                        preload="none"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </div>
                    <button
                      type="button"
                      className="lf-media-play-btn"
                      onClick={(e) => { e.stopPropagation(); toggleCardPlay('web'); }}
                      aria-label={webPlaying ? 'Pause demo' : 'Play demo'}
                    >
                      <span className="lf-play-icon">
                        <i className={`fas fa-${webPlaying ? 'pause' : 'play'}`} />
                      </span>
                    </button>
                  </div>
                  <div
                    className="lf-card-footer"
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveModal('web')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveModal('web'); } }}
                  >
                    <i className="fas fa-expand-alt" aria-hidden="true" />
                    {t('landing.view_demo', 'View demo')}
                  </div>
                </div>

                {/* Plugin */}
                <div className="lf-card lf-funnel-card" role="listitem">
                  <div className="lf-funnel-card-header">
                    <div className="lf-funnel-card-icon" aria-hidden="true">
                      <i className="fas fa-puzzle-piece" />
                    </div>
                    <div className="lf-funnel-card-titles">
                      <div className="lf-funnel-card-label">{t('landing.funnel_card_plugin_label', 'Any website')}</div>
                      <h3>{t('landing.funnel_card_plugin_title', 'Plugin')}</h3>
                    </div>
                  </div>
                  <p>
                    {t(
                      'landing.funnel_card_plugin_desc',
                      'Hover any text to decode it instantly. Works on every site you visit.',
                    )}
                  </p>
                  <div
                    className={`lf-funnel-card-media${pluginPlaying ? ' is-playing' : ''}`}
                    onMouseEnter={() => handleCardHover('plugin', true)}
                    onMouseLeave={() => handleCardHover('plugin', false)}
                    onClick={() => setActiveModal('plugin')}
                  >
                    <div className="lf-browser-mock">
                      <div className="lf-browser-bar" aria-hidden="true">
                        <span className="dot red" />
                        <span className="dot yellow" />
                        <span className="dot green" />
                        <span className="addr" />
                      </div>
                      <video
                        ref={pluginVideoRef}
                        src={pluginDemoVideoSm}
                        poster={pluginDemoSmPoster}
                        loop
                        muted
                        playsInline
                        preload="none"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                      />
                    </div>
                    <button
                      type="button"
                      className="lf-media-play-btn"
                      onClick={(e) => { e.stopPropagation(); toggleCardPlay('plugin'); }}
                      aria-label={pluginPlaying ? 'Pause demo' : 'Play demo'}
                    >
                      <span className="lf-play-icon">
                        <i className={`fas fa-${pluginPlaying ? 'pause' : 'play'}`} />
                      </span>
                    </button>
                  </div>
                  <div
                    className="lf-card-footer"
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveModal('plugin')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveModal('plugin'); } }}
                  >
                    <i className="fas fa-expand-alt" aria-hidden="true" />
                    {t('landing.view_demo', 'View demo')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ 3. Interactive Playground ═══════════ */}
        <section className="lf-section" id="demo" aria-labelledby="demo-heading">
          <div className="container">
            <h2 id="demo-heading" className="lf-section-title" style={{ textAlign: 'center' }}>
              {t('landing.demo_title', 'Try it now')}
            </h2>
            <p className="lf-section-subtitle" style={{ textAlign: 'center', margin: '0 auto 2rem' }}>
              {t(
                'landing.funnel_demo_subtitle',
                'Type or paste any text, then adjust the reading format in real time.',
              )}
            </p>

            {/* App-like browser frame */}
            {/* File drop overlay disabled for now — re-enable drag handlers + overlay below when ready to support file import from landing */}
            <div
              className="lf-app-frame"
              // onDragEnter={handleDragEnter}
              // onDragLeave={handleDragLeave}
              // onDragOver={handleDragOver}
              // onDrop={handleDrop}
            >
              {/* <div className="lf-drop-overlay" aria-hidden="true">
                <i className="fas fa-cloud-upload-alt lf-drop-icon" />
                <span className="lf-drop-label">{t('landing.drop_label', 'Drop your file to get started')}</span>
                <span className="lf-drop-hint">{t('landing.drop_hint', 'PDF, image, or text file')}</span>
              </div> */}

              {/* Browser chrome */}
              <div className="lf-app-chrome" aria-hidden="true">
                <span className="dot red" />
                <span className="dot yellow" />
                <span className="dot green" />
                <span className="lf-app-chrome-url">
                  <i className="fas fa-lock" style={{ fontSize: '0.6rem', opacity: 0.5, marginRight: '0.35rem' }} />
                  dysgoapp.com
                </span>
              </div>

              {/* Toolbar */}
              <div className="lf-app-toolbar" role="toolbar" aria-label="Reading format controls">
                <PlaygroundControls
                  highlightPunctuation={highlightPunctuation}
                  onHighlightPunctuation={setHighlightPunctuation}
                />
              </div>

              {/* Single editable striped view */}
              <div className="lf-app-body lf-reader-demo" role="region" aria-label="Live reading preview">
                <div className="lf-app-pane">
                  <div className="lf-app-pane-label" style={{ color: 'var(--primary)' }}>
                    <i className="fas fa-book-reader" aria-hidden="true" />
                    {t('landing.demo_reading_label', 'Reading view')}
                  </div>
                  <div className="lf-app-reading-scroll" style={{ padding: '0' }}>
                    <StripedTextarea
                      value={sampleText}
                      onChange={(v) => {
                        userEditedRef.current = true;
                        setSampleText(v);
                      }}
                      rows={12}
                      highlightPunctuation={highlightPunctuation}
                      ariaLabel={t('landing.demo_input_label', 'Your text')}
                      style={{
                        fontFamily: 'var(--bs-body-font-family)',
                        padding: '16px',
                        minHeight: '100%',
                        border: 'none',
                        borderRadius: 0,
                      }}
                    />
                  </div>
                  <div className="lf-app-pane-actions">
                    <button
                      type="button"
                      className="lf-app-btn-ghost"
                      style={{ color: 'var(--primary)', fontWeight: 700 }}
                      onClick={() => {
                        userEditedRef.current = false;
                        setSampleText(sampleTextInitial);
                      }}
                    >
                      <i className="fas fa-undo" aria-hidden="true" />
                      {t('landing.demo_reset', 'Reset sample text')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer hint */}
              <div className="lf-app-footer">
                <span>
                  <i className="fas fa-info-circle" aria-hidden="true" style={{ marginRight: '0.35rem' }} />
                  {t('landing.demo_footer_hint', 'This is a live preview — adjust controls above to customize your reading experience')}
                </span>
                <a href="/app/auth/login" className="lf-app-footer-cta">
                  {t('landing.demo_open_app', 'Open full app')}
                  <i className="fas fa-arrow-right" aria-hidden="true" style={{ marginLeft: '0.35rem', fontSize: '0.7rem' }} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ 3b. Plugin Demo Strip (moved AFTER playground) ═══════════ */}
        <section className="lf-section lf-plugin-section" aria-labelledby="plugin-strip-heading">
          <div className="container">
            <div className="lf-plugin-strip">
              <h2 id="plugin-strip-heading" className="lf-section-title">
                {t('landing.plugin_strip_title', 'See the plugin in action')}
              </h2>
              <p className="lf-section-subtitle" style={{ margin: '0 auto 1.5rem', textAlign: 'center' }}>
                {t('landing.plugin_strip_subtitle', 'Enable the live demo, then hover any sentence on this page to see it decoded.')}
              </p>

              <button
                type="button"
                className={`btn btn-${pluginDemoEnabled ? 'outline-' : ''}primary lf-plugin-demo-btn`}
                onClick={() => setPluginDemoEnabled((v) => !v)}
                style={{ borderRadius: '999px', fontWeight: 700, marginBottom: '1.25rem' }}
                aria-pressed={pluginDemoEnabled}
              >
                <i className={`fas fa-${pluginDemoEnabled ? 'toggle-on' : 'toggle-off'}`} aria-hidden="true" style={{ marginRight: '0.4rem' }} />
                {pluginDemoEnabled
                  ? t('landing.plugin_demo_btn_on', 'Plugin demo is ON — hover any text')
                  : t('landing.plugin_demo_btn', 'Enable plugin demo')}
              </button>

              <div className="lf-plugin-strip-body">
                <p style={{ margin: 0 }}>
                  {t(
                    'landing.plugin_strip_p1',
                    'Reading dense paragraphs can be exhausting, especially for people with dyslexia. Small fonts and tight spacing make it even harder to keep your place. DysGo fixes that — just hover over any text and a clean, formatted popup appears. No copy-pasting, no switching tabs — reading comfort wherever you browse.',
                  )}
                </p>
              </div>
              <p className="lf-plugin-strip-hint" aria-live="polite">
                {pluginDemoEnabled ? (
                  <>
                    <i className="fas fa-mouse-pointer" style={{ marginRight: '0.35rem' }} aria-hidden="true" />
                    {t('landing.plugin_strip_hint_on', 'Hover over any sentence above to see the plugin in action')}
                  </>
                ) : (
                  <span className="lf-hint-pill">
                    <i className="fas fa-arrow-up" style={{ marginRight: '0.35rem' }} aria-hidden="true" />
                    {t('landing.plugin_strip_hint_off', 'Click the button above to activate the live plugin demo')}
                  </span>
                )}
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════ 4. Testimonial Chat ═══════════ */}
        <section className="lf-section" id="testimonials" aria-labelledby="testimonials-heading">
          <div className="container">
            <h2 id="testimonials-heading" className="lf-section-title" style={{ textAlign: 'center' }}>
              {t('landing.testimonials_title', 'Real readers, real results')}
            </h2>
            <p className="lf-section-subtitle" style={{ textAlign: 'center', margin: '0 auto 2.5rem' }}>
              {t('landing.testimonials_subtitle', 'Hear from students who changed the way they read.')}
            </p>

            <div className="lf-chat" role="list" aria-label="User testimonials">
              {/* User quote */}
              <div className="lf-chat-row" role="listitem">
                <img
                  src={testimonialBoy}
                  alt="Illustrated cartoon boy with books (drawn avatar, not a real photo, for privacy)"
                  className="lf-chat-avatar"
                  loading="lazy"
                  width={48}
                  height={48}
                />
                <div className="lf-stripe-preview lf-chat-bubble">
                  {/* Desktop: one line per stripe */}
                  <div className="lf-stripe-preview-line lf-quote-desktop" style={{ fontStyle: 'italic' }}>
                    {t('landing.quote_text', 'it\'s like having a Swiss Army knife for reading in my pocket!')}
                  </div>
                  {/* Mobile: split across stripes */}
                  <div className="lf-stripe-preview-line lf-quote-mobile" style={{ fontStyle: 'italic' }}>
                    {t('landing.quote_line1', 'it\'s like having a Swiss Army knife')}
                  </div>
                  <div className="lf-stripe-preview-line lf-quote-mobile" style={{ fontStyle: 'italic' }}>
                    {t('landing.quote_line2', 'for reading in my pocket!')}
                  </div>
                  <div className="lf-stripe-preview-line">
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--lf-muted)' }}>
                      {t('landing.quote_author', '- Alex C. (User)')}
                    </span>
                  </div>
                </div>
              </div>
              {/* DysGo reply */}
              <div className="lf-chat-row lf-chat-right" role="listitem">
                <img
                  src="/assets/ic_launcher.png"
                  alt="DysGo app icon"
                  className="lf-chat-avatar lf-chat-avatar-logo"
                  loading="lazy"
                  width={48}
                  height={48}
                />
                <div className="lf-chat-bubble lf-chat-bubble-dysgo">
                  {t('landing.quote_reply', 'This made reading feel possible again.')}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ 4b. About / Mission ═══════════ */}
        <section className="lf-section" id="about" aria-labelledby="about-heading">
          <div className="container">
            <div className="lf-about-grid">
              <div className="lf-about-text">
                <div className="lf-eyebrow">
                  <span aria-hidden="true" style={{ color: 'var(--primary)' }}>
                    <i className="fas fa-heart" style={{ fontSize: '0.72rem' }} />
                  </span>
                  {t('landing.about_eyebrow', 'Our mission')}
                </div>
                <h2 id="about-heading">
                  {t('landing.about_title', 'Reading for everyone \u2014 not just some.')}
                </h2>
                <p>
                  {t(
                    'landing.about_desc',
                    'We believe reading comfort is a right, not a luxury. DysGo was born from a simple idea: if spacing and rhythm can transform the reading experience for people with dyslexia, why not make it available to everyone?',
                  )}
                </p>
                <a href="#download" className="btn btn-primary" style={{ borderRadius: '999px', fontWeight: 700 }}>
                  {t('landing.about_cta', 'Get started free')}
                </a>
              </div>
              <div
                className="lf-about-img-wrap rounded overflow-hidden shadow-sm"
              >
                <img
                  src="/assets/img/taking-picture.jpg"
                  alt={t('landing.about_img_alt', 'Woman reading comfortably')}
                  className="h-100 img-fluid"
                  loading="lazy"
                  width={380}
                  height={475}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ 5. Download CTA ═══════════ */}
        <section className="lf-band lf-download" id="download" aria-labelledby="download-heading">
          <div className="container">
            <h2 id="download-heading" className="lf-section-title" style={{ color: '#fff' }}>
              {t('landing.download_title', 'Download DysGo')}
            </h2>
            <p className="lf-section-subtitle" style={{ color: 'rgba(255,255,255,0.85)', margin: '0 auto 1.5rem', textAlign: 'center' }}>
              {t('landing.download_subtitle', 'Start with the mobile app, or open the web app in your browser.')}
            </p>
            <div className="d-flex flex-wrap justify-content-center" style={{ gap: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-white btn-lg"
                onClick={() => setBetaModalOpen(true)}
                aria-label={t('landing.download_google_alt', 'Google Play - join closed beta')}
              >
                <img src="/assets/img/google-play-badge.svg" alt="Download from Play Store" loading="lazy" width={108} height={32} style={{ height: 32 }} />
              </button>
              <button
                type="button"
                className="btn btn-white btn-lg"
                onClick={() => setBetaModalOpen(true)}
                aria-label={t('landing.download_appstore_alt', 'App Store - join closed beta')}
              >
                <img src="/assets/img/app-store-badge.svg" alt="Download from the App Store" loading="lazy" width={96} height={32} style={{ height: 32 }} />
              </button>
              <a className="btn btn-outline-white btn-lg" href="/app/auth/login" style={{ borderRadius: '999px' }}>
                {t('landing.download_open_web', 'Open web app')}
              </a>
            </div>
          </div>
        </section>

        {/* ═══════════ Demo Modal ═══════════ */}
        {activeModal && (
          <div
            className="lf-modal-backdrop"
            onClick={() => setActiveModal(null)}
            role="dialog"
            aria-modal="true"
            aria-label={activeModal ? MODAL_CONTENT[activeModal].title : undefined}
          >
            <div className="lf-modal" onClick={(e) => e.stopPropagation()}>
              <div className="lf-modal-head">
                <b>
                  {activeModal && MODAL_CONTENT[activeModal].title}
                </b>
                <button type="button" className="lf-modal-close" onClick={() => setActiveModal(null)} aria-label={t('landing.close', 'Close')}>
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
              <div className="lf-modal-body">
                {/* Media side */}
                <div className={`lf-modal-media${modalPlaying ? ' is-playing' : ''}`}>
                  {activeModal && (
                    <>
                      {MODAL_CONTENT[activeModal].mediaMock === 'phone' ? (
                        <div className="lf-phone-mock">
                          <video ref={modalVideoRef} src={MODAL_CONTENT[activeModal].videoSrc} autoPlay loop muted playsInline aria-label={MODAL_CONTENT[activeModal].videoAlt} />
                        </div>
                      ) : (
                        <div className="lf-browser-mock">
                          <div className="lf-browser-bar" aria-hidden="true">
                            <span className="dot red" /><span className="dot yellow" /><span className="dot green" /><span className="addr" />
                          </div>
                          <video ref={modalVideoRef} src={MODAL_CONTENT[activeModal].videoSrc} autoPlay loop muted playsInline style={{ display: 'block', width: '100%', height: 'auto' }} aria-label={MODAL_CONTENT[activeModal].videoAlt} />
                        </div>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    className="lf-media-play-btn"
                    onClick={toggleModalPlay}
                    aria-label={modalPlaying ? 'Pause demo' : 'Play demo'}
                  >
                    <span className="lf-play-icon">
                      <i className={`fas fa-${modalPlaying ? 'pause' : 'play'}`} />
                    </span>
                  </button>
                </div>

                {/* Explanation side */}
                <div className="lf-modal-explain">
                  {activeModal && (
                    <>
                      <h4>{MODAL_CONTENT[activeModal].heading}</h4>
                      <p>{MODAL_CONTENT[activeModal].body}</p>
                      <div className="lf-stripe-preview" aria-hidden="true">
                        {MODAL_CONTENT[activeModal].bullets.map((line, i) => (
                          <div key={i} className="lf-stripe-preview-line">{line}</div>
                        ))}
                      </div>
                      {MODAL_CONTENT[activeModal].ctaAction ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ borderRadius: '999px', fontWeight: 700, marginTop: 'auto' }}
                          onClick={MODAL_CONTENT[activeModal].ctaAction}
                        >
                          {MODAL_CONTENT[activeModal].ctaLabel}
                        </button>
                      ) : (
                        <a
                          href={MODAL_CONTENT[activeModal].ctaUrl}
                          className="btn btn-primary"
                          style={{ borderRadius: '999px', fontWeight: 700, marginTop: 'auto' }}
                          onClick={() => setActiveModal(null)}
                        >
                          {MODAL_CONTENT[activeModal].ctaLabel}
                        </a>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ Beta Modal ═══════════ */}
        {/* <BetaModal open={betaModalOpen} onClose={closeBetaModal} t={t} /> */}
        <BetaModal open={false} onClose={closeBetaModal} t={t} />

        {/* ═══════════ Drop Toast ═══════════ */}
        <DropToast
          visible={dropToastVisible}
          message={t('landing.drop_toast', 'Sign in to import your file')}
        />
      </main>
      </ConfigProvider>

      {/* ═══════════ 6. Footer ═══════════ */}
      <PublicFooter />
    </div>
  );
};

export default Landing2026Funnel2;
